import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { useNavigate } from 'react-router-dom'; 
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 

const rapportsBilansAlimentairesData = [
    {
    title: "Rapport de l'analyse des bilans alimentaires du Burundi – Édition 2023",
    principalLink: "/documents/IndicesIPPAB/Rapport_BA_2023.pdf",
     image: "assets/cover/cover.jpg",
    description: "En 2023, la disponibilité alimentaire végétale par habitant était principalement constituée de racines et tubercules (301,9 kg) et de céréales (90 kg), affichant une hausse générale par rapport à 2022, tandis que la disponibilité en produits animaux était dominée par le lait (21,2 kg) qui enregistre une baisse cette année.",
    date: "2025-06-15",
    publicationDate: "2025-06-15",
  },
   {
    title: "Rapport de l'analyse des bilans alimentaires du Burundi – Édition 2022",
    principalLink: "/documents/IndicesIPPAB/RAPPORT_BA_2022.pdf",
    image: "assets/cover/cover.jpg",
    description: " L'analyse du bilan alimentaire 2022 révèle que la disponibilité alimentaire annuelle par habitant repose principalement sur les racines et tubercules (297,5 kg) et les céréales (76 kg), tandis que le lait domine les produits animaux (22,6 kg), affichant une légère hausse par rapport à 2021.",
    date: "2025-05-15",
    publicationDate: "2025-05-15",
  },
   {
    title: "Dépliant : Analyse des bilans alimentaires du Burundi (2022)",
    principalLink: "/documents/rapports/Burundi_Dépliant-Bilans-Alimentaires_Final_07Avril2023.pdf",
    description: "En 2020-2021, le Burundi affiche une autosuffisance alimentaire de 99,1%, avec une disponibilité moyenne de 2 257,5 kcal/pers/jour (dont 55,1% provenant du maïs), et une faible dépendance aux importations (7,4% en 2020, 3,9% en 2021).",
    image: "assets/cover/cover.jpg",
    date: "2023-05-15",
    publicationDate: "2023-05-15",
  },
  {
    title: "Rapport final d'analyse des bilans alimentaires du Burundi 2020-2021",
    principalLink: "/documents/rapports/Rapport-Final_BA-Burundi_17Apr2023.pdf",
    description: "Les DEA passent de 2 241 kcal (2020) à 2 274 kcal (2021), avec une contribution stable des végétaux (97,3-97,6%), confirmant le régime majoritairement végétal du Burundi (moins de 3% de produits animaux).",
    image: "assets/cover/cover.jpg",
    date: "2023-05-15",
    publicationDate: "2023-05-15",
  },
   {
    title: "Rapport d'Analyse des Bilans Alimentaires du Burundi 2020-2021",
    link: "/documents/rapports/Burundi_Dépliant-Bilans-Alimentaires_Final_07Avril2023.pdf",
    description: "En 2020-2021, le Burundi affiche une autosuffisance alimentaire de 99,1%, avec une disponibilité moyenne de 2 257,5 kcal/pers/jour (dont 55,1% provenant du maïs), et une faible dépendance aux importations (7,4% en 2020, 3,9% en 2021).",
    image: "assets/cover/cover.jpg",
    date: "2023-05-15",
    publicationDate: "2023-05-15",
    year: 2023
  },
];

function RapportsBilansAlimentaires() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [filteredRapports, setFilteredRapports] = useState(rapportsBilansAlimentairesData);
  const navigate = useNavigate(); 

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year === 'Tous') {
      setFilteredRapports(rapportsBilansAlimentairesData);
    } else {
      const filtered = rapportsBilansAlimentairesData.filter((rapport) => {
        const rapportYear = new Date(rapport.date).getFullYear();
        return rapportYear === parseInt(year);
      });
      setFilteredRapports(filtered);
    }
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: {
        main: '#388E3C',
      },
      background: {
        default: '#f5f5f5',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />

        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
            Rapports d'Analyse des Bilans Alimentaires du Burundi
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Les rapports d'analyse des bilans alimentaires présentent une évaluation complète de la disponibilité alimentaire au Burundi. 
            Ils quantifient les ressources alimentaires issues de la production nationale, des importations et des stocks, 
            et les comparent avec les utilisations alimentaires pour déterminer la disponibilité alimentaire par habitant. 
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année"
            >
              <MenuItem value="Tous">Tous les rapports</MenuItem>
              {[...new Set(rapportsBilansAlimentairesData.map((rapport) => new Date(rapport.date).getFullYear()))]
                .sort((a, b) => b - a) 
                .map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredRapports.slice(0, visibleCount).map((rapport, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={rapport.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 71, height: 110, m: 2 }}
                        image={rapport.image}
                        alt={rapport.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                        <a
                          href={rapport.principalLink}
                          rel="noopener noreferrer"
                          target="_blank"
                          style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                        >
                          {<DownloadIcon />}{rapport.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {rapport.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(rapport.publicationDate).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredRapports.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Rapports
            </Button>
          )}
          
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les rapports d'analyse des bilans alimentaires sont publiés annuellement, généralement au cours du premier semestre de l'année suivant celle de référence. 
          </Typography>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default RapportsBilansAlimentaires;